<template>
    <div class="space-y-4">
        <h2 class="max-w-120 text-xl font-semibold text-center">{{ $t('views.feed.mixes.editMixModal.header') }}</h2>
        <div class="flex items-center border border-dark-gray-800 border-opacity-20 rounded-lg pl-3 pr-1 py-2 space-x-2 w-120 focus-within:border-opacity-50">
            <Icon class="h-3.5 w-3.5 flex-shrink-0 opacity-50" :src="IconSource.Edit"></Icon>
            <input ref="nameInput" v-model="editedName" type="text" class="flex-1 outline-none bg-transparent text-sm min-w-0 text-gray-400 focus:text-gray-800 transition-colors" />
            <button
                type="button"
                :class="{ 'opacity-25 cursor-default': !editedNameChanged || isSavingName, 'hover:bg-gray-100': editedNameChanged && !isSavingName }"
                class="p-1.5 rounded transition-colors flex-shrink-0"
                :disabled="!editedNameChanged || isSavingName"
                @click="onSaveNameClicked"
            >
                <Icon class="h-4 w-4" :src="IconSource.Check"></Icon>
            </button>
        </div>
        <div class="w-120">
            <CustomSelect
                :value="mix.category?.id ?? ''"
                :prepend="$t('views.feed.mixes.table.headings.category')"
                label=""
                :items="mixCategories"
                item-text="name"
                item-value="id"
                :show-required-indication="false"
                :allow-empty="true"
                @update:value="onCategoryChanged"
            ></CustomSelect>
        </div>
        <div v-if="loggedInUserCanReadFeedPrices">
            <table class="w-120 table-headers-medium">
                <thead>
                    <tr class="px-6 flex mt-2 rounded text-dark-gray-800 text-opacity-50">
                        <th class="flex flex-1">
                            <span class="mx-auto">{{ priceHeader }}</span>
                        </th>
                        <th class="flex flex-1">
                            <span class="mx-auto">{{ $t('views.feed.mixes.editMixModal.table.headers.validFrom') }}</span>
                        </th>
                        <th class="flex w-12"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="mix.type.id === 2" class="rounded flex py-4 px-6 my-4 bg-feed-600 bg-opacity-60 border-2 border-opacity-10 rounded-lg">
                        <td class="flex flex-1 my-auto break-words">
                            <span class="mx-auto">
                                <SimpleInput v-model:value="priceToAdd.price" class="w-20" type="number" :placeholder="$t('views.feed.mixes.editMixModal.table.newPricePlaceholder')"></SimpleInput>
                            </span>
                        </td>
                        <td class="flex flex-1 my-auto break-words">
                            <span class="mx-auto w-32">
                                <FeedDatePicker v-model:value="priceToAdd.valid_from" :placeholder="$t('views.feed.mixes.editMixModal.table.newValidFromPlaceholder')" :disabled="false"></FeedDatePicker>
                            </span>
                        </td>
                        <td class="flex w-12 my-auto">
                            <button
                                type="button"
                                :class="{ 'opacity-25 cursor-default': !validPriceToAdd, 'bg-primary-800 text-white rounded font-medium': validPriceToAdd }"
                                class="mx-auto px-2 py-1"
                                @click="onAddPriceClicked"
                            >
                                {{ $t('views.feed.mixes.editMixModal.table.addPriceButtonLabel') }}
                            </button>
                        </td>
                    </tr>
                    <tr v-for="(price, index) of prices" :key="index" class="rounded flex py-4 px-6 my-4 odd:bg-dark-gray-100 border-2 border-opacity-10 rounded-lg">
                        <td class="flex flex-1 my-auto break-words">
                            <span class="mx-auto">{{ $n(price.price, 'price') }}</span>
                        </td>
                        <td class="flex flex-1 my-auto break-words">
                            <span class="mx-auto">{{ $d(new Date(price.valid_from), 'short') }}</span>
                        </td>
                        <td class="flex w-12 my-auto">
                            <button v-if="mix.type.id === 2 && mix.prices && mix.prices.length > 1" type="button" class="mx-auto" @click="onDeletePriceClicked(price)">
                                <Icon class="w-6" :src="IconSource.Delete"></Icon>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="flex space-x-4">
            <DeleteMixConfirmationModal :mix="computedValue" @submit="$emit('submit')">
                <template #default="scope">
                    <DeleteButton @click="scope.open">
                        {{ $t('views.feed.mixes.deleteMixModal.deleteButtonLabel') }}
                    </DeleteButton>
                </template>
            </DeleteMixConfirmationModal>
            <button
                type="button"
                :disabled="isDeactivating"
                :class="isDeactivating ? 'bg-gray-300 text-white cursor-default' : 'bg-orange-400 text-orange-500 bg-opacity-25 hover:bg-opacity-35 cursor-pointer'"
                class="rounded-lg py-2 px-3 flex transition-colors"
                @click="onToggleMixActiveClicked"
            >
                <span class="m-auto pr-2"> 
                    <Icon
                        class="w-6"
                        :src="mix.is_active ? IconSource.Archive : IconSource.Unarchive"
                        :style="isDeactivating
                            ? 'filter: brightness(0) invert(1) opacity(0.6)'
                            : 'filter: brightness(0) saturate(100%) invert(54%) sepia(91%) saturate(736%) hue-rotate(344deg) brightness(103%) contrast(101%)'
                        "
                    ></Icon>
                </span>
                <span>{{ mix.is_active ? $t('views.feed.mixes.editMixModal.deactivateButtonLabel') : $t('views.feed.mixes.editMixModal.activateButtonLabel') }}</span>
            </button>
            <div>
                <MixFarmsModal :mix="mix">
                    <template #default="scope">
                        <AddButton @click="scope.open">
                            {{ $t('views.feed.mixes.editMixModal.mixFarms.mixFarmsButtonLabel') }}
                        </AddButton>
                    </template>
                </MixFarmsModal>
            </div>
            <div v-if="showMixMaterials">
                <MixRawMaterialsModal :mix="mix">
                    <template #default="scope">
                        <AddButton @click="scope.open">
                            {{ $t('views.feed.mixes.editMixModal.mixRawMaterials.mixRawMaterialsButtonLabel') }}
                        </AddButton>
                    </template>
                </MixRawMaterialsModal>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Form } from '@makeabledk/vue-ui/support/http';
import { ActionType } from '@/plugins/store/actions';
import { store } from '@/plugins/store';
import { Mix, MixCategory, Price, MixTypeId } from '@/types';
import DeleteMixConfirmationModal from '@/views/dashboard/feed/feedMixes/editMixModal/DeleteMixConfirmationModal.vue';
import MixFarmsModal from '@/views/dashboard/feed/feedMixes/editMixModal/MixFarmsModal.vue';
import MixRawMaterialsModal from '@/views/dashboard/feed/feedMixes/editMixModal/MixRawMaterialsModal.vue';
import FeedDatePicker from '@/components/ui/FeedDatePicker.vue';
import AddButton from '@/components/ui/AddButton.vue';
import DeleteButton from '@/components/ui/DeleteButton.vue';
import SimpleInput from '@/components/ui/SimpleInput.vue';
import CustomSelect from '@/components/ui/CustomSelect.vue';

export default defineComponent({
    components: { DeleteMixConfirmationModal, MixFarmsModal, MixRawMaterialsModal, FeedDatePicker, AddButton, DeleteButton, SimpleInput, CustomSelect },
    props: {
        mix: {
            type: Object as () => Form<Mix> & Mix,
            required: true,
        },
    },
    emits: ['submit', 'update:mix', 'close'],
    data() {
        return {
            priceToAdd: {
                price: '' as number | string,
                valid_from: '',
            },
            isDeactivating: false,
            editedName: this.mix.name as string,
            isSavingName: false,
        };
    },
    watch: {
        'mix.name'(newName: string) {
            if (!this.editedNameChanged) {
                this.editedName = newName;
            }
        },
    },
    computed: {
        editedNameChanged(): boolean {
            return this.editedName !== this.mix.name;
        },
        showMixMaterials(): boolean {
            return this.$props.mix.type.id !== MixTypeId.COMPOUND_MIX;
        },
        computedValue: {
            get(): Form<Mix> & Mix {
                return this.mix;
            },
            set(newValue: any) {
                this.$emit('update:mix', newValue);
            },
        },
        farms(): { id: number; name: string }[] {
            return store.state.farms.map((farm) => ({ name: farm.name, id: farm.farm_id }));
        },
        validPriceToAdd(): boolean {
            return Boolean(typeof this.priceToAdd.price === 'number' && typeof this.priceToAdd.valid_from === 'string' && this.priceToAdd.valid_from.length);
        },
        prices(): Price[] {
            return [...this.mix.prices].sort((a, b) => Date.parse(a.valid_from) - Date.parse(b.valid_from));
        },
        priceHeader() {
            return store.getters.priceCurrencyPerWeightUnitDisplayText
                ? this.$t('views.feed.mixes.editMixModal.table.headers.priceUnit').replace('%VALUE%', store.getters.priceCurrencyPerWeightUnitDisplayText)
                : this.$t('views.feed.mixes.editMixModal.table.headers.price');
        },
        loggedInUserCanReadFeedPrices(): boolean {
            return store.getters.loggedInUserCanReadFeedPrices;
        },
        mixCategories(): MixCategory[] {
            return store.state.mixCategories;
        },
    },
    methods: {
        // Assigning or clearing the category re-groups feed deliveries when consumption is
        // derived, so the API triggers a full key-figure recomputation for the directory.
        async onCategoryChanged(categoryId: number | '') {
            const nextCategoryId = categoryId === '' ? null : categoryId;
            if (nextCategoryId === (this.mix.category?.id ?? null)) {
                return;
            }

            const category = this.mixCategories.find((currentCategory) => currentCategory.id === nextCategoryId) ?? null;

            await store.dispatch(ActionType.UpdateFeedMix, {
                form: {
                    id: this.mix.id,
                    update: {
                        name: this.mix.name,
                        surcharge: this.mix.surcharge ?? null,
                        is_active: this.mix.is_active,
                        category_id: nextCategoryId,
                    },
                },
            });

            this.computedValue.category = category;
            store.dispatch(ActionType.GetMixes, { options: { ignoreCache: true } });
        },
        async onSaveNameClicked() {
            if (!this.editedNameChanged || this.isSavingName) {
                return;
            }

            this.isSavingName = true;

            try {
                await store.dispatch(ActionType.UpdateFeedMix, {
                    form: {
                        id: this.mix.id,
                        update: {
                            name: this.editedName,
                            surcharge: this.mix.surcharge ?? null,
                            is_active: this.mix.is_active,
                            // Sent unchanged - the API applies category_id unconditionally, so
                            // omitting it here would clear the category on a rename.
                            category_id: this.mix.category?.id ?? null,
                        },
                    },
                });

                this.computedValue.name = this.editedName;
                (this.$refs.nameInput as HTMLInputElement).blur();
                store.dispatch(ActionType.GetMixes, { options: { ignoreCache: true } });
            } finally {
                this.isSavingName = false;
            }
        },
        async onAddPriceClicked() {
            if (!this.validPriceToAdd) {
                return;
            }

            const form = new Form({ mix: this.mix, price: this.priceToAdd as Price });
            await store.dispatch(ActionType.CreateMixPrice, { form: form as any });

            if (!form.errors.hasErrors()) {
                this.priceToAdd.price = '';
                this.priceToAdd.valid_from = '';
                store.dispatch(ActionType.GetMixes, { options: { ignoreCache: true } });
            }
        },
        async onDeletePriceClicked(price: Price) {
            await store.dispatch(ActionType.DeleteMixPrice, { form: { mix: this.mix, price } });
            store.dispatch(ActionType.GetMixes, { options: { ignoreCache: true } });
        },
        async onToggleMixActiveClicked() {
            if (this.isDeactivating) {
                return;
            }

            const nextActiveState = !this.mix.is_active;
            this.isDeactivating = true;

            try {
                await store.dispatch(ActionType.UpdateFeedMix, {
                    form: {
                        id: this.mix.id,
                        update: {
                            name: this.mix.name,
                            surcharge: this.mix.surcharge ?? null,
                            is_active: nextActiveState,
                            category_id: this.mix.category?.id ?? null,
                        },
                    },
                });

                this.computedValue.is_active = nextActiveState;
                this.$emit('close');
                setTimeout(() => {
                    store.dispatch(ActionType.GetMixes, { options: { ignoreCache: true } });
                }, 0);
            } finally {
                this.isDeactivating = false;
            }
        },
    },
});
</script>
